<script lang="ts">
    import Editor from '@tinymce/tinymce-svelte'
    import { CONTENT_IMAGE_MAX_HEIGHT, CONTENT_IMAGE_MAX_WIDTH } from '../utils/image-limits'

    export let value = ''
    /** Habilita o botão de imagem na barra de ferramentas (ver upload pro S3 abaixo). */
    export let allowImage = false

    let editorRef: any = null
    let fileInput: HTMLInputElement

    /**
     * Insere conteúdo na posição atual do cursor (o TinyMCE preserva a última seleção mesmo depois
     * de perder o foco, então funciona ao clicar num botão fora do editor). Devolve `false` se o
     * editor ainda não terminou de inicializar, pra quem chama poder tratar.
     */
    export function insertAtCursor(content: string): boolean {
        if (!editorRef) return false
        editorRef.insertContent(content)
        // O wrapper (@tinymce/tinymce-svelte) só atualiza o `bind:value` nos eventos
        // 'change input undo redo'. Reemitir 'change' garante a sincronização mesmo que o
        // insertContent não dispare nenhum deles — sem passar por setContent, que jogaria o cursor
        // de volta pro início. Se o evento já tiver ocorrido, isto vira no-op (conteúdo idêntico).
        editorRef.dispatch?.('change')
        editorRef.focus()
        return true
    }

    const MIME_TO_EXTENSION: Record<string, string> = {
        'image/png': 'png',
        'image/jpeg': 'jpg',
        'image/gif': 'gif',
        'image/webp': 'webp',
        'image/bmp': 'bmp',
    }

    // A imagem nunca fica como base64 no conteúdo salvo: sobe pro S3 e só a URL final vai pro
    // banco. Além do peso no banco, o sanitizeHtml (utils/sanitize-html.ts) remove `src` com
    // protocolo `data:`, então uma imagem em base64 simplesmente não apareceria ao exibir/imprimir.
    //
    // Decisão deliberada: `upload` (bucket público, URL direta e permanente) e não
    // `uploadToSignedLink`. O conteúdo aqui é do próprio consultório — texto de apoio, ilustração,
    // orientação — e não resposta do paciente, então não há conteúdo sensível a proteger.
    //
    // O fluxo pré-assinado também não serviria sem trabalho extra: ele grava no bucket protegido e
    // devolve a referência `/files?file=...`, que a tools-api re-assina na leitura percorrendo
    // `document.answers` e os `headerFooter` — e esta imagem mora dentro do HTML em
    // `blocks[].data.text`, que a travessia não visita. Ficaria 401 no `<img src>`.
    async function uploadImage(blobInfo: any): Promise<string> {
        const blob = blobInfo.blob()
        const original = 'data:' + blob.type + ';base64,' + blobInfo.base64()
        // Imagem colada aqui vai para o corpo do documento e é decodificada em toda renderização,
        // inclusive na geração do PDF. Ver utils/image-limits.ts.
        const dataUrl = await window.api.utils.images.resizeBase64(original, CONTENT_IMAGE_MAX_WIDTH, CONTENT_IMAGE_MAX_HEIGHT)
        // `folderName` é sobrescrito por 'front-upload' dentro de FilesService.upload().
        const { url } = await window.api.events.files.upload({
            content: dataUrl,
            extension: MIME_TO_EXTENSION[blob.type] ?? 'png',
            folderName: 'forms-v2',
        })
        return url
    }

    const baseToolbar =
        'fontfamily fontsize | forecolor backcolor | bold italic underline strikethrough | alignnone alignleft aligncenter alignright | numlist bullist | link table'

    // `const`, não `$:` — o conf é lido uma vez na inicialização do TinyMCE; recalculá-lo (o que
    // aconteceria com `$:`, já que `fileInput` só é preenchido depois pelo bind:this) faria o
    // editor remontar e perder o conteúdo em edição.
    const conf = {
        menubar: false,
        content_css: '/tinymce.css',
        plugins: allowImage ? ['lists', 'table', 'link', 'image'] : ['lists', 'table', 'link'],
        a11y_advanced_options: true,
        branding: false,
        toolbar: (allowImage ? baseToolbar + ' image' : baseToolbar) + ' | removeformat',
        // O padrão do TinyMCE no desktop é 'floating': o "..." abre um painel **destacado** do editor,
        // que não acompanha a rolagem. Com vários editores na mesma tela (um por bloco), a barra de um
        // bloco continuava visível ao rolar até outro, e abrir o "..." do segundo dava a impressão de
        // duas barras soltas — sem pista de qual pertencia a qual editor.
        // 'sliding' faz o excedente deslizar dentro da própria barra do editor: fica sempre colado ao
        // bloco a que pertence e rola junto com ele. Não usei 'wrap' (que eliminaria o "...") porque
        // bloco de 1/3 de largura quebraria a barra em várias linhas, comendo altura em todos eles.
        toolbar_mode: 'sliding',
        ...(allowImage
            ? {
                  file_picker_types: 'image',
                  // Sobe assim que a imagem entra no editor (seleção, colar ou arrastar), em vez de
                  // deixar acumular blob/base64 pra resolver só no salvamento.
                  automatic_uploads: true,
                  paste_data_images: true,
                  images_upload_handler: uploadImage,
                  // Mesmo fluxo do editor da versão anterior (customer-service/elements/tiny-custom.svelte):
                  // um <input type="file"> próprio alimenta o blobCache, e o images_upload_handler
                  // acima é quem de fato manda pro S3.
                  file_picker_callback: (cb: (url: string, meta?: any) => void) => {
                      fileInput.onchange = () => {
                          const file = fileInput.files?.[0]
                          if (!file) return
                          const reader = new FileReader()
                          reader.onload = () => {
                              const id = 'blobid' + new Date().getTime()
                              const blobCache = editorRef?.editorUpload?.blobCache
                              if (!blobCache) return
                              const base64 = (reader.result as string).split(',')[1]
                              const blobInfo = blobCache.create(id, file, base64)
                              blobCache.add(blobInfo)
                              cb(blobInfo.blobUri(), { title: file.name })
                          }
                          reader.readAsDataURL(file)
                      }
                      fileInput.value = ''
                      fileInput.click()
                  },
              }
            : {}),
        init_instance_callback: (editor: any) => {
            editorRef = editor
        },
    }
</script>

{#if allowImage}
    <input type="file" accept="image/*" bind:this={fileInput} class="hidden-file-input" />
{/if}
<Editor {conf} bind:value scriptSrc="/tinymce/tinymce.min.js" />

<style>
    .hidden-file-input {
        position: absolute;
        top: -100000px;
        left: -100000px;
    }
</style>
